import { Radio, RadioGroup as RadioGroupInformed } from 'informed';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Icon } from 'components/common/icons';

export type RadioGroupOption = {
  disabled?: boolean;
  id: string;
  label: React.ReactNode;
  labelAdditionalContent?: React.ReactNode;
  tooltip?: string;
  tooltipHideIcon?: boolean;
  value?: any;
};

interface Props extends React.HTMLAttributes<HTMLElement> {
  children?: React.ReactNode;
  darkMode?: boolean;
  error?: string;
  field: string;
  initialValue?: any;
  inline?: boolean;
  inlineBreakpoint?: number;
  inlineSpacing?: string;
  labelStyle?: React.CSSProperties;
  onChange?: (value: any) => void;
  options?: RadioGroupOption[];
  spacing?: string;
  stopPropagation?: boolean;
  style?: React.CSSProperties;
  styleAsCards?: boolean;
  validate?: (value: any, values?: any) => undefined | string;
}

const RadioGroup = ({
  children,
  darkMode,
  error,
  field,
  initialValue,
  inline,
  inlineBreakpoint = breakpoints.medium,
  inlineSpacing = '25px',
  labelStyle,
  onChange,
  options,
  spacing = '5px',
  stopPropagation = true,
  style,
  styleAsCards,
  validate
}: Props) => {
  const { theme } = useReduxState();

  let color = 'rgba(0, 0, 0, 0.1)';
  let iconColor = theme.successTextColor;
  let activeColor = theme.successColor;
  let outlineColor = 'rgba(0, 0, 0, 0.15)';
  let labelColor = theme.textColorLight;
  if (darkMode) {
    color = 'rgba(255, 255, 255, 0.5)';
    iconColor = theme.successTextColorDark;
    activeColor = theme.successColorDark;
    outlineColor = 'rgba(255, 255, 255, 0.2)';
    labelColor = '#fff';
  }

  const handleClick = (ev) => ev.stopPropagation();

  return (
    <div className="radio-group" style={style}>
      <RadioGroupInformed
        field={field}
        onValueChange={onChange}
        validate={validate}
        {...(initialValue !== undefined ? { initialValue } : {})}
      >
        {options
          ? options.map((option) => (
              <div
                className={`radio ${inline ? ' inline' : ''}`}
                key={option.id}
                {...(stopPropagation ? { onClick: handleClick } : {})}
              >
                <Radio
                  disabled={option.disabled}
                  field={field}
                  id={option.id}
                  value={option.value}
                />
                <div className={`label ${styleAsCards ? 'label-card' : ''}`}>
                  <label
                    htmlFor={option.id}
                    style={labelStyle}
                    {...(option.tooltip
                      ? {
                          'aria-label': option.tooltip,
                          'data-tooltip': option.tooltip
                        }
                      : {})}
                  >
                    {option.label}
                    {option.tooltip && !option.tooltipHideIcon ? (
                      <Icon
                        icon="question-circle"
                        style={{
                          fontSize: '1.4rem',
                          marginLeft: 5
                        }}
                      />
                    ) : null}
                  </label>

                  {option.labelAdditionalContent ? (
                    <div className="additional-content">
                      {option.labelAdditionalContent}
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          : null}

        {children ? children : null}
      </RadioGroupInformed>

      {error ? <div className="error-message">{error}</div> : null}

      <style jsx>{`
        .radio {
          position: relative;
        }
        .radio:not(:first-child) {
          margin-top: ${spacing};
        }
        .radio > :global(input) {
          opacity: 0;
          overflow: hidden;
          position: absolute;
          height: 0.1px;
          width: 0.1px;
          z-index: -1;
        }
        .radio > :global(input + .label label) {
          color: ${labelColor};
          cursor: pointer;
          display: inline-block;
          font-size: 1.5rem;
          font-weight: 400;
          padding-left: 24px;
          position: relative;
          white-space: normal;
          width: auto;
          word-wrap: break-word;
        }
        .radio > :global(input + .label label::before) {
          background-color: ${color};
          border: 0;
          border-radius: 10px;
          box-shadow: none;
          content: '';
          display: inline-block;
          left: 0;
          height: 18px;
          padding: 0;
          position: absolute;
          top: 1px;
          width: 18px;
          z-index: 1;
        }
        .radio > :global(input + .label label::after) {
          background-color: transparent;
          border-radius: 5px;
          content: '';
          display: inline-block;
          height: 6px;
          left: 6px;
          position: absolute;
          top: 7px;
          transition: background-color 0.1s;
          width: 6px;
          z-index: 1;
        }
        .radio > :global(input + .label label:hover::after) {
          background-color: rgba(0, 0, 0, 0.2);
        }
        .radio > :global(input:checked + .label label::before) {
          background-color: ${activeColor};
        }
        .radio > :global(input:checked + .label label::after) {
          background-color: ${iconColor};
        }
        .radio > :global(input:disabled + .label label) {
          cursor: default;
          opacity: 0.5;
        }
        .radio > :global(input:disabled + .label label:hover::before) {
          color: ${color};
        }
        .radio > :global(input:disabled + .label label:hover::after) {
          background-color: transparent;
        }
        .radio > :global(input:disabled:checked + .label label:hover::before) {
          color: ${activeColor};
        }
        .radio > :global(input:disabled:checked + .label label:hover::after) {
          background-color: ${iconColor};
        }
        .radio > :global(input:focus + .label label::before),
        .radio > :global(input:focus:checked + .label label::before) {
          box-shadow: 0 0 0 5px ${outlineColor};
        }
        .radio > :global(input + .label-card label) {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: 7px;
          display: block;
          padding: 10px 40px 10px 10px;
        }
        .radio > :global(input:checked + .label-card label) {
          border-color: #a1a8aa;
        }
        .radio > :global(input + .label-card label::before) {
          top: calc(50% - 9px);
          left: auto;
          right: 16px;
        }
        .radio > :global(input + .label-card label::after) {
          top: calc(50% - 3px);
          left: auto;
          right: 22px;
        }
        .additional-content {
          font-weight: 400;
          padding-left: 24px;
          margin: 5px 0;
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          padding: 3px;
        }
        @media (min-width: ${inlineBreakpoint}px) {
          .radio.inline {
            display: inline-block;
            margin-top: 0;
          }
          .radio.inline:not(:first-child) {
            margin-left: ${inlineSpacing};
          }
        }
      `}</style>
    </div>
  );
};

export default RadioGroup;
